import { useQuery } from '@tanstack/react-query';
import { getDocs } from 'firebase/firestore';

import { useAuth } from '@/app/auth';
import { mergeWidgetConfigs, type WidgetDocCandidate } from '@/features/dashboard/mergeWidgetConfigs';
import { widgetRegistry } from '@/features/dashboard/registry';
import { userWidgetsCollection } from '@/lib/firestorePaths';

const STALE_TIME_MS = 5 * 60_000;
const EMPTY_CANDIDATES: WidgetDocCandidate[] = [];

async function fetchWidgetCandidates(uid: string): Promise<WidgetDocCandidate[]> {
  const snapshot = await getDocs(userWidgetsCollection(uid));
  return snapshot.docs.map((d) => ({ id: d.id, data: d.data() }));
}

/**
 * Loads every widget's stored config doc and merges it onto the registry.
 * Returns all registered widgets (config only) — placement is owned by the
 * fixed layout in `DashboardGrid`, not by this hook.
 */
export default function useDashboardWidgets() {
  const { status, user } = useAuth();
  const uid = user?.uid ?? '';
  const enabled = status === 'authorized' && !!uid;

  return useQuery({
    queryKey: ['dashboard', 'widgets', uid],
    queryFn: () => fetchWidgetCandidates(uid),
    enabled,
    staleTime: STALE_TIME_MS,
    placeholderData: EMPTY_CANDIDATES,
    select: (candidates) => mergeWidgetConfigs(candidates, widgetRegistry),
  });
}
